import { FormControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';

import {
    CouponsService,
    UpdateCouponRequestModel,
    CouponDetailInterface,
    CouponDiscountTypeEnum,
    CouponPaymentMethodEnum,
    CouponStatusEnum,
    CreateCouponPlanScopeInterface,
    TradingPlanService
} from '@spa/api';
import { ServerResponse } from '@spa/api';

import {
    CouponDiscountValueValidators,
    CouponFormMessages,
    CouponPortalIdValue,
    CouponPlanScopeValidatorKey,
    UpdateCouponControls,
    UpdateCouponFormControlsInterface
} from 'appAssets/descriptors/panel-coupons.descriptor';
import { BaseControl, Helper } from 'appShared/classes';

import { CouponControlErrorMessages } from './coupon-form-errors';
import { CouponCapValue, CouponRedemptionCapsValid } from './coupon-redemption-caps';
import { CouponPlanScopesFromControlValues } from './coupon-plan-scopes';
import { CouponPlanScopesForm } from './coupon-plan-scopes.form';

export class CouponUpdateForm {
    private couponsService: CouponsService;
    private tradingPlanService: TradingPlanService;
    private couponId: number;
    private subscriptions: Subscription[] = [];
    public formGroup: FormGroup;
    public controls: UpdateCouponFormControlsInterface;
    public planScopesForm: CouponPlanScopesForm;

    constructor(
        couponsService: CouponsService,
        tradingPlanService: TradingPlanService,
        coupon: CouponDetailInterface,
        couponId: number,
        canEdit: boolean = true
    ) {
        this.couponsService = couponsService;
        this.tradingPlanService = tradingPlanService;
        this.couponId = couponId;
        this.initForm(coupon, canEdit);
    }

    /**
     * The backend requires at least one plan scope, and the plan list and plan-type picker together
     * make up that collection — so the rule cannot live on a single control's validators.
     */
    private validatePlanScopes(): boolean {
        this.controls.planScopePlanTypeIds.resetErrors();

        if (this.getPlanScopes().length > 0) {
            return true;
        }

        this.controls.planScopePlanTypeIds.setError(CouponFormMessages()[CouponPlanScopeValidatorKey]);

        return false;
    }

    initForm(coupon: CouponDetailInterface, canEdit: boolean = true): void {
        this.formGroup = new FormGroup({});
        this.controls = UpdateCouponControls(coupon);
        this.planScopesForm = new CouponPlanScopesForm(this.tradingPlanService, coupon);

        const values = Object.values(this.controls) as BaseControl<any>[];
        values.forEach((control: BaseControl<any>) => {
            this.formGroup.addControl(control.name, new FormControl(control.value, control.validators));
        });

        const discountTypeControl = this.formGroup.get(UpdateCouponRequestModel.getDiscountTypeSelector());
        this.subscriptions.push(
            discountTypeControl.valueChanges.subscribe((discountType: number) => this.discountTypeChanged(discountType))
        );

        this.applyEditPolicy(coupon?.status, canEdit);
    }

    destroy(): void {
        this.subscriptions.forEach((subscription: Subscription) => subscription?.unsubscribe());
        this.subscriptions = [];
    }

    /**
     * Mirrors `CouponEditPolicy` on the backend, which rejects the whole update when a field the
     * current status freezes has changed:
     *
     * Inactive — every field is editable.
     * Active   — description, end date, and both redemption caps.
     * Expired  — every field except the code.
     */
    applyEditPolicy(status: number, canEdit: boolean = true): void {
        const editable = canEdit ? this.getEditableControls(status) : [];

        (Object.values(this.controls) as BaseControl<any>[]).forEach((control: BaseControl<any>) => {
            const isEditable = editable.indexOf(control.name) >= 0;
            const formControl = this.formGroup.get(control.name);

            control.disabled = !isEditable;

            if (isEditable) {
                formControl.enable();
            } else {
                formControl.disable();
            }
        });

        // The plan list follows the plan-type picker: both make up `plan_scopes`.
        this.planScopesForm.setDisabled(editable.indexOf(this.controls.planScopePlanTypeIds.name) < 0);
    }

    getEditableControls(status: number): string[] {
        const allControls = (Object.values(this.controls) as BaseControl<any>[])
            .map((control: BaseControl<any>) => control.name)
            .filter((name: string) => name !== this.controls.paymentMethod.name);

        if (status === CouponStatusEnum.Inactive) {
            return allControls;
        }

        if (status === CouponStatusEnum.Expired) {
            return allControls.filter((name: string) => name !== this.controls.code.name);
        }

        return [
            this.controls.description.name,
            this.controls.endDateTimeUtc.name,
            this.controls.globalRedemptionCap.name,
            this.controls.perUserRedemptionCap.name
        ];
    }

    createRequest(): ServerResponse<CouponDetailInterface> {
        if (!this.formGroup.dirty && !this.planScopesForm.form.dirty) {
            return null;
        }

        // Disabled controls are excluded from `formGroup.value`, but the endpoint replaces the whole
        // coupon — so the frozen fields have to be sent back as they are.
        const v = this.formGroup.getRawValue();

        const model = new UpdateCouponRequestModel(
            v[UpdateCouponRequestModel.getCodeSelector()],
            v[UpdateCouponRequestModel.getDiscountTypeSelector()],
            // The amount input holds a formatted string ("1 000.50"), the endpoint wants a number.
            Helper.getAmountValue(v[UpdateCouponRequestModel.getDiscountValueSelector()]),
            v[UpdateCouponRequestModel.getCurrencyCodeSelector()],
            v[UpdateCouponRequestModel.getUserEligibilityTypeSelector()],
            v[UpdateCouponRequestModel.getDescriptionSelector()] || null,
            v[UpdateCouponRequestModel.getPaymentMethodSelector()],
            CouponCapValue(v[UpdateCouponRequestModel.getGlobalRedemptionCapSelector()]),
            CouponCapValue(v[UpdateCouponRequestModel.getPerUserRedemptionCapSelector()]),
            v[UpdateCouponRequestModel.getStartDateTimeUtcSelector()] || null,
            v[UpdateCouponRequestModel.getEndDateTimeUtcSelector()] || null,
            CouponPortalIdValue(v[UpdateCouponRequestModel.getPortalIdSelector()]),
            this.getPlanScopes(),
            v[UpdateCouponRequestModel.getApplicableToTypeSelector()]
        );

        return this.couponsService.update(this.couponId, model);
    }

    getPlanScopes(): CreateCouponPlanScopeInterface[] {
        return CouponPlanScopesFromControlValues(
            this.planScopesForm.getPlanIds(),
            this.formGroup.getRawValue()[this.controls.planScopePlanTypeIds.name]
        );
    }

    discountTypeChanged(discountType: number): void {
        this.controls.discountValue.isPercentage = discountType === CouponDiscountTypeEnum.Percentage;

        const formControl = this.formGroup.get(UpdateCouponRequestModel.getDiscountValueSelector());

        formControl.setValidators(CouponDiscountValueValidators(discountType));
        formControl.updateValueAndValidity();
    }

    validateForm(
        messagesFunction: (controlsLabel?: string, controlLength?: number) => { [key: string]: string }
    ): boolean {
        const values = Object.values(this.controls) as BaseControl<any>[];
        const results = values.map((control: BaseControl) => {
            const formControl = this.formGroup.controls[control.name];

            if (formControl && formControl.invalid) {
                control.errors = CouponControlErrorMessages(control, formControl.errors, messagesFunction);

                return false;
            }

            return true;
        });

        return [
            ...results,
            this.planScopesForm.validateForm(messagesFunction),
            this.validatePlanScopes(),
            CouponRedemptionCapsValid(
                this.formGroup,
                this.controls.globalRedemptionCap,
                this.controls.perUserRedemptionCap,
                messagesFunction
            )
        ].indexOf(false) < 0;
    }
}
